/**
 * FmProductPage — sealed owner-story archetype.
 *
 * The shell owns the immersive hero, responsive chapter order, section
 * rendering, transitions, and state presentation. Routes pass a hero
 * descriptor plus owner chapters of product capabilities and shared sections;
 * they cannot pass JSX, layout props, chart options, raw links, or media URLs.
 */
import React from 'react';
import { FmAccordion } from './FmAccordion';
import { FmProductChrome } from './FmProductChrome';
import { FmProductHero } from './FmProductHero';
import { FmSectionBand } from './FmReportComposition';
import { useFmResponsive } from './FmResponsive';
import { ProductAction } from './ProductAction';
import { ProductSectionRenderer } from './FmProductPage.sections';
import { FmProductFooter } from './FmProductFooter';
import type {
  FmProductSectionDescriptor,
  FmProductActionDescriptor,
  FmProductChapterDescriptor,
  FmProductChromeDescriptor,
  FmProductFooterDescriptor,
  FmProductHeroDescriptor,
} from './FmProductPage.types';
import { validateFmProductPageProps } from './FmProductPage.validation';
import './FmProductPage.css';
import './FmProductPricing.css';

export type {
  FmProductActionDescriptor,
  FmProductAvailableState,
  FmProductCapability,
  FmProductChromeAuthDialog,
  FmProductChromeDescriptor,
  FmProductChromeIdentity,
  FmProductChromeLanguageControl,
  FmProductChromeLanguageOption,
  FmProductChromeNavigationItem,
  FmProductCatalogDecisionCtaSection,
  FmProductCatalogDecisionRecord,
  FmProductChapterDescriptor,
  FmProductChapterTransition,
  FmProductContextualActionsSection,
  FmProductConversionRole,
  FmProductEmptyState,
  FmProductErrorState,
  FmProductHeroDescriptor,
  FmProductHeroForecast,
  FmProductHeroForecastCopy,
  FmProductHeroForecastMetric,
  FmProductHeroForecastPoint,
  FmProductHeroForecastSeries,
  FmProductHeroLeadInteraction,
  FmProductFooterDescriptor,
  FmProductLoadingState,
  FmProductLockedState,
  FmProductMediaFact,
  FmProductPartialState,
  FmProductReadyState,
  FmProductRegisteredMediaSection,
  FmProductSectionDescriptor,
  FmProductSectionRole,
  FmProductSectionState,
  FmProductSharedSection,
  FmProductStaleState,
  FmProductUnavailableState,
} from './FmProductPage.types';

export interface FmProductPageProps {
  /** Optional renderer-owned public chrome for product conversion routes. */
  chrome?: FmProductChromeDescriptor;
  /** Optional archetype-owned public trust and legal closure. */
  footer?: FmProductFooterDescriptor;
  /** Sealed immersive opener; the shell resolves its governed media and cue. */
  hero: FmProductHeroDescriptor;
  /** Ordered owner chapters. */
  chapters: readonly [FmProductChapterDescriptor, ...FmProductChapterDescriptor[]];
  /** Accessible name for the product story main landmark. */
  ariaLabel: string;
  'data-testid'?: string;
}

function orderedChapters(
  chapters: FmProductPageProps['chapters'],
  mobile: boolean,
): FmProductChapterDescriptor[] {
  const ordered = [...chapters];
  if (mobile) ordered.sort((left, right) => left.mobileOrder - right.mobileOrder);
  return ordered;
}

function mobilePrimaryAction(
  hero: FmProductHeroDescriptor,
  chapters: readonly FmProductChapterDescriptor[],
): FmProductActionDescriptor | undefined {
  if (hero.primaryAction) return hero.primaryAction;

  const conversionChapter = chapters.find((chapter) => chapter.conversionRole === 'convert');
  if (!conversionChapter) return undefined;

  for (const section of conversionChapter.sections) {
    if (
      section.capability !== 'contextual-actions' ||
      !('actions' in section) ||
      (section.role !== 'lead' && section.role !== 'action')
    ) {
      continue;
    }
    const action = section.actions.find(
      (candidate) => candidate.intent === 'primary' && !candidate.disabledReason,
    );
    if (action) return action;
  }
  return undefined;
}

function productSectionBands(sections: FmProductChapterDescriptor['sections']) {
  const bands: {
    film: boolean;
    audience?: 'owner' | 'transaction';
    label?: string;
    sections: FmProductSectionDescriptor[];
  }[] = [];
  for (const section of sections) {
    const film =
      section.capability === 'registered-media' &&
      'mediaKind' in section &&
      section.mediaKind === 'video';
    const catalog =
      section.capability === 'catalog-decision-cta' && 'catalog' in section
        ? section.catalog
        : null;
    const audience = catalog?.audience;
    const previous = bands[bands.length - 1];
    if (previous && previous.film === film && previous.audience === audience)
      previous.sections.push(section);
    else
      bands.push({
        film,
        audience,
        label: catalog?.groupLabel,
        sections: [section],
      });
  }
  return bands;
}

const ProductChapter: React.FC<{
  chapter: FmProductChapterDescriptor;
  compact: boolean;
}> = ({ chapter, compact }) => {
  const chapterTone = chapter.conversionRole === 'compare' ? ('deep' as const) : undefined;
  const bands = productSectionBands(chapter.sections);
  const filmOpener = bands[0]?.film;
  const opener = (
    <header
      className="fm-product-chapter__opener"
      data-testid={`product-chapter-opener-${chapter.id}`}
    >
      <h2>{chapter.literalAnswer}</h2>
    </header>
  );
  return (
    <article
      id={chapter.id}
      className="fm-product-chapter"
      data-conversion-role={chapter.conversionRole}
      data-dominant-section={chapter.dominantSectionId}
      data-story-surface={chapterTone ?? 'light'}
      data-testid={`product-chapter-${chapter.id}`}
    >
      {!filmOpener && opener}
      {bands.map((band, index) => (
        <FmSectionBand
          key={band.sections[0].id}
          tone={band.film ? 'deep' : (chapterTone ?? 'plain')}
          density="compact"
          className={
            band.film
              ? 'fm-product-chapter__movement-band fm-product-chapter__film-band'
              : 'fm-product-chapter__movement-band'
          }
        >
          {filmOpener && index === 0 && opener}
          {band.label && <p className="fm-product-pricing-group-label">{band.label}</p>}
          <div className="fm-product-chapter__movement" data-pricing-audience={band.audience}>
            {band.sections.map((section) => {
              const renderer = (
                <ProductSectionRenderer
                  section={section}
                  dominant={section.id === chapter.dominantSectionId}
                />
              );
              const disclosed =
                compact &&
                section.role !== 'lead' &&
                section.role !== 'action' &&
                section.capability !== 'registered-media' &&
                section.capability !== 'catalog-decision-cta';
              return disclosed ? (
                <FmAccordion
                  key={section.id}
                  plain
                  lazy
                  className="fm-product-chapter__disclosure"
                  items={[
                    {
                      key: section.id,
                      label: section.title,
                      children: renderer,
                      'data-testid': `product-disclosure-${section.id}`,
                    },
                  ]}
                />
              ) : (
                <React.Fragment key={section.id}>{renderer}</React.Fragment>
              );
            })}
          </div>
        </FmSectionBand>
      ))}
    </article>
  );
};

export function FmProductPage(props: FmProductPageProps): React.ReactElement {
  validateFmProductPageProps(props);
  const { hero, chrome, footer, chapters, ariaLabel, 'data-testid': testId } = props;
  const tier = useFmResponsive();
  const ordered = React.useMemo(
    () => orderedChapters(chapters, tier === 'mobile'),
    [chapters, tier],
  );
  const mobileAction = React.useMemo(() => mobilePrimaryAction(hero, ordered), [hero, ordered]);
  const conversionChapterId = React.useMemo(
    () => ordered.find((chapter) => chapter.conversionRole === 'convert')?.id,
    [ordered],
  );
  const [mobileDockSuppressed, setMobileDockSuppressed] = React.useState(false);
  const pageRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (
      tier !== 'mobile' ||
      !mobileAction ||
      !conversionChapterId ||
      typeof IntersectionObserver === 'undefined'
    ) {
      setMobileDockSuppressed(false);
      return undefined;
    }

    const page = pageRef.current;
    if (!page) return undefined;

    const conversion = page.querySelector(`#${CSS.escape(conversionChapterId)}`);
    const productFooter = page.querySelector('.fm-product-footer');
    const productHero = page.querySelector('.fm-product-hero');
    const localInteractionTargets = page.querySelectorAll(
      [
        '.fm-product-section[data-product-live-map="true"]',
        '.fm-product-section[data-product-carousel="true"]',
      ].join(','),
    );
    const localActionTargets = Array.from(page.querySelectorAll('.fm-product-section')).filter(
      (section) => section.querySelector('.fm-product-actions__rail'),
    );
    const targets = [
      conversion,
      productFooter,
      productHero,
      ...localInteractionTargets,
      ...localActionTargets,
    ].filter((target): target is Element => Boolean(target));
    const visibleTargets = new Set<Element>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleTargets.add(entry.target);
          else visibleTargets.delete(entry.target);
        });
        setMobileDockSuppressed(visibleTargets.size > 0);
      },
      { threshold: 0.01 },
    );
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [conversionChapterId, mobileAction, tier]);
  return (
    <main
      ref={pageRef}
      className="fm-product-page"
      data-fm-topbar-scope
      aria-label={ariaLabel}
      data-testid={testId ?? 'fm-product-page'}
      data-tier={tier}
      data-mobile-primary={mobileAction ? 'true' : 'false'}
      data-mobile-dock-visible={
        tier === 'mobile' && mobileAction && !mobileDockSuppressed ? 'true' : 'false'
      }
    >
      {chrome ? <FmProductChrome descriptor={chrome} /> : null}
      <FmProductHero descriptor={hero} firstChapterId={ordered[0].id} />
      <div className="fm-product-page__chapters">
        {ordered.map((chapter) => (
          <ProductChapter key={chapter.id} chapter={chapter} compact={tier !== 'desktop'} />
        ))}
      </div>
      {footer ? <FmProductFooter footer={footer} /> : null}
      {tier === 'mobile' && mobileAction && !mobileDockSuppressed ? (
        <div
          className="fm-product-page__mobile-primary"
          data-testid="product-mobile-primary-action"
        >
          <ProductAction action={mobileAction} />
        </div>
      ) : null}
    </main>
  );
}

FmProductPage.displayName = 'FmProductPage';

export default FmProductPage;
